import { resolveDatePreset } from "./datePresetUtils";
import widgetDataStore from "../store/widgetDataStore";
import { outboxStoreData } from "Pages/Dashboard/outbox/outboxStore";
import { defaultEmailType, defaultChannel } from "Static/outboxConstant";
import dialerStoreData from "Stores/DialerData";
import { callsStore } from "Stores/Calls";
import taskStoreData from "Stores/taskData";
import userDetail from "Stores/userDetail";
import { calendarState } from "Pages/Dashboard/settings/Calendar/State";
import DateUtils from "Utils/DateUtils";
import Utils from "Utils/utils";
import { toasterState } from "Components/common/toaster";

const GOOGLE_CALENDAR_URL = "https://calendar.google.com/calendar";
const OUTLOOK_CALENDAR_URL = "https://outlook.office.com/calendar";
const OUTLOOK_TYPE_VALUES = ["outlook", "microsoft", "outlook_oauth", "outlook_oath"];

// Per-widget "View Records" destination. See Z_tasks/SG-2116-view-records.md for the
// reasoning behind each mapping.
const WIDGET_TARGETS = {
  EMAILS_SENT: { type: "outbox", activeFilter: { name: "delivered", id: "SENT" }, dateFieldName: "DELIVERED" },
  EMAIL_OPEN_RATE: { type: "outbox", activeFilter: { name: "opened", id: "OPEN_COUNT" }, dateFieldName: "OPENED" },
  EMAIL_REPLY_RATE: { type: "outbox", activeFilter: { name: "replied", id: "REPLY_COUNT" }, dateFieldName: "REPLIED" },
  BOUNCES: { type: "outbox", activeFilter: { name: "bounced", id: "BOUNCE_COUNT" }, dateFieldName: "BOUNCED" },
  MANUAL_EMAILS_SENT: {
    type: "outbox",
    activeFilter: { name: "delivered", id: "SENT" },
    dateFieldName: "DELIVERED",
    emailType: ["MANUAL_EMAIL"]
  },
  SEQUENCE_EMAILS_SENT: {
    type: "outbox",
    activeFilter: { name: "delivered", id: "SENT" },
    dateFieldName: "DELIVERED",
    emailType: ["AUTO_EMAIL"]
  },
  // Used by LatestRepliesWidget's own internal "View More" button, not the generic
  // WidgetCard footer - see WidgetGrid.js's onViewRecords exclusion list.
  LATEST_REPLIES: { type: "outbox", activeFilter: { name: "replied", id: "REPLY_COUNT" }, dateFieldName: "REPLIED" },

  CALLS_CONNECTED: { type: "calls", stageDisplayName: "Connected" },
  // No disposition lookup - "Total" is a hardcoded card in CallsOverview.js, not a team stage.
  TOTAL_CALLS_MADE: { type: "calls", stageDisplayName: null },

  CONNECTIONS_SENT: { type: "outbox", activeFilter: { name: "requests_sent", id: "LI_CR_SENT_COUNT" } },
  CONNECTIONS_ACCEPTED: { type: "outbox", activeFilter: { name: "accepted", id: "LI_CR_ACCEPTED_COUNT" } },
  // Inbox -> Sent tab, filtered to LinkedIn only. No date filter - consistent with the other
  // LinkedIn widgets (Connections Sent/Accepted), which also don't carry the dashboard's date range.
  TOTAL_LINKEDIN_MESSAGES_SENT: {
    type: "outbox",
    activeFilter: { name: "delivered", id: "SENT" },
    channel: ["LINKEDIN"]
  },
  // Inbox -> Replied tab, filtered to LinkedIn only. No date filter, same reasoning as above.
  LINKEDIN_REPLIES_RECEIVED: {
    type: "outbox",
    activeFilter: { name: "replied", id: "REPLY_COUNT" },
    channel: ["LINKEDIN"]
  },

  TASKS_DUE: { type: "tasks", statusTab: "due" },
  TASKS_COMPLETED: { type: "tasks", statusTab: "completed" },

  MEETINGS_BOOKED: { type: "calendar" }
};

const buildSnappedDateRange = activeDateFilter => {
  const { fromDate, toDate } = resolveDatePreset(activeDateFilter);
  return {
    fromDate: DateUtils.getTimeStamp(fromDate, "start"),
    toDate: DateUtils.getTimeStamp(toDate, "end")
  };
};

const buildOwnersOverride = memberIds => {
  if (!memberIds?.length) return null;
  const teamMembers = userDetail?.userInfo?.hasTeamMember || [];
  if (!teamMembers?.length) return null;
  return teamMembers.map(member => ({
    ...member,
    checked: memberIds.includes(member?.memberId)
  }));
};

const navigateToOutbox = (history, target) => {
  const { activeFilter, dateFieldName, emailType, channel } = target;
  const filterObject = {};
  const filterList = [];

  if (dateFieldName) {
    const { fromDate, toDate } = buildSnappedDateRange(widgetDataStore.activeDateFilter);
    filterObject.dates = [
      { fieldName: dateFieldName, fromDate: new Date(fromDate), toDate: new Date(toDate), operator: "BETWEEN" }
    ];
    filterList.push("dates");
  }
  if (emailType?.length) {
    filterObject.emailType = defaultEmailType.map(item => ({ ...item, checked: emailType.includes(item.id) }));
    filterList.push("emailType");
  }
  if (channel?.length) {
    filterObject.channel = defaultChannel.map(item => ({ ...item, checked: channel.includes(item.id) }));
    filterList.push("channel");
  }
  const owners = buildOwnersOverride(widgetDataStore.activeMemberIds);
  if (owners) {
    filterObject.owners = owners;
    filterList.push("owners");
  }

  outboxStoreData.setCurrentPage(1);
  outboxStoreData.setActiveFilter(activeFilter);
  outboxStoreData.setFilterObject(filterObject);
  outboxStoreData.setFilterList(filterList);
  outboxStoreData.setTempFilterList(filterList);
  history.push("/inbox");
};

const navigateToCalls = async (history, target) => {
  let callDispositionId = null;
  let currentTab = "TOTAL";

  if (target.stageDisplayName) {
    if (!dialerStoreData.stageList?.length) {
      await dialerStoreData.getCallStageList();
    }
    const stage = (dialerStoreData.stageList || []).find(
      s => (s?.displayName || s?.name || "").toLowerCase() === target.stageDisplayName.toLowerCase()
    );
    if (!stage) {
      toasterState.setToastMsg(`Could not find the "${target.stageDisplayName}" call stage`, "fail");
      return;
    }
    callDispositionId = stage.id;
    currentTab = stage.name;
  }

  // CallsFilter.js seeds its local date-filter state from `callsStore.filterValues` at mount
  // time (`useState(callsStore.filterValues)`), then drives its actual fromDate/to computation
  // off that local "customDate" mode - so the override has to be set here, before navigating,
  // not as a fromDate/to pair on filterPayload (which gets recomputed and overwritten on mount).
  const { fromDate, toDate } = buildSnappedDateRange(widgetDataStore.activeDateFilter);
  const startDate = new Date(fromDate);
  const endDate = new Date(toDate);
  callsStore.setFilterValues({
    activeDateFilter: "customDate",
    customDate: [{ startDate, endDate, key: "selection" }],
    customDateFormat: { start: startDate, end: endDate },
    customDateValue: `${Utils.formatDate(startDate)} -  ${Utils.formatDate(endDate)}`
  });
  callsStore.setFilterPayload({ callDispositionId, pageNo: 1 });
  callsStore.setInputValues({ currentTab, callDispositionId });
  history.push("/calls/overview");
};

const navigateToTasks = (history, target) => {
  history.push("/tasks", { payload: { statusTab: target.statusTab } });
};

const navigateToCalendar = async () => {
  const account = await calendarState.getCalendarConnnectedAccount();
  if (!account?.email) {
    toasterState.setToastMsg("Connect a calendar in Settings to view booked meetings.", "fail");
    return;
  }
  const type = (account?.type || "").toLowerCase();
  const url =
    type === "gmail_oath"
      ? GOOGLE_CALENDAR_URL
      : OUTLOOK_TYPE_VALUES.includes(type)
        ? OUTLOOK_CALENDAR_URL
        : null;
  if (!url) {
    toasterState.setToastMsg("Unrecognized calendar provider.", "fail");
    return;
  }
  Utils.windowRedirect(url, "_blank", true);
};

export const isViewRecordsAvailable = widgetId => !!WIDGET_TARGETS[widgetId];

export const handleViewRecordsClick = async (widgetId, history) => {
  const target = WIDGET_TARGETS[widgetId];
  if (!target) return false;
  switch (target.type) {
    case "outbox":
      navigateToOutbox(history, target);
      return true;
    case "calls":
      await navigateToCalls(history, target);
      return true;
    case "tasks":
      navigateToTasks(history, target);
      return true;
    case "calendar":
      await navigateToCalendar();
      return true;
    default:
      return false;
  }
};
